/**
 * @fileoverview Ponto de entrada da aplicação React.
 *
 * Responsabilidades:
 * - Inicializa o Sentry para monitoramento de erros
 * - Configura auto-recovery para falhas de lazy-load (chunks obsoletos após deploy)
 * - Monta o componente raiz <App /> no DOM
 * - Exibe fallback de emergência caso a montagem falhe
 */
import { createRoot } from "react-dom/client";
import { initSentry } from "@/lib/sentry";
import App from "./App.tsx";
import "./index.css";
// driver.js CSS is lazy-loaded with OnboardingWizard component

// Initialize Sentry error tracking before anything else
initSentry();

// Apply the persisted theme synchronously BEFORE React mounts. Avoids the
// flash-of-light-theme on dark users (the slice's initializer runs after
// React first paints, so we'd see a frame in light mode otherwise).
(() => {
  try {
    const raw = localStorage.getItem('bl-theme');
    const mode = raw ? JSON.parse(raw) : 'system';
    const isDark = mode === 'dark' || (mode === 'system'
      && window.matchMedia?.('(prefers-color-scheme: dark)').matches);
    document.documentElement.classList.toggle('dark', !!isDark);
  } catch { /* localStorage/matchMedia indisponíveis: fica o tema padrão */ }
})();

// Auto-recovery: if a lazy-loaded chunk fails (stale service worker cache after deploy),
// unregister the SW and reload so the browser fetches fresh assets.
// Auto-recovery: if a lazy-loaded chunk fails, unregister SW and reload ONCE
window.addEventListener("unhandledrejection", (event) => {
  if (
    event.reason instanceof TypeError &&
    event.reason.message.includes("Failed to fetch dynamically imported module")
  ) {
    // Circuit breaker: only reload once to prevent infinite loop
    const reloadKey = '__chunk_reload_attempted';
    if (sessionStorage.getItem(reloadKey)) return; // Already tried, don't loop
    sessionStorage.setItem(reloadKey, '1');
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((reg) => reg.unregister());
        window.location.reload();
      });
    } else {
      window.location.reload();
    }
  }
});

try {
  createRoot(document.getElementById("root")!).render(<App />);
} catch (err) {
  console.error('Fatal: App failed to mount', err);
  const root = document.getElementById("root");
  if (root) {
    // Safe DOM construction — no innerHTML with dynamic content
    const container = document.createElement('div');
    container.style.cssText = 'padding:2rem;font-family:monospace;color:#ef4444;';
    const heading = document.createElement('h2');
    heading.textContent = 'Erro ao inicializar o app';
    const pre = document.createElement('pre');
    pre.style.cssText = 'white-space:pre-wrap;word-break:break-all;';
    pre.textContent = 'Ocorreu um erro inesperado. Recarregue a página.';
    const btn = document.createElement('button');
    btn.style.cssText = 'margin-top:1rem;padding:0.5rem 1rem;cursor:pointer;';
    btn.textContent = 'Recarregar';
    btn.addEventListener('click', () => location.reload());
    container.append(heading, pre, btn);
    root.textContent = '';
    root.appendChild(container);
  }
}
